import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import useSnackbar from './useSnackbar'
import useLoading from './useLoading'
import { API_BASE_URL } from '../constants/system'
import { BatchImportAnalyzeResponse, BatchImportPreviewResponse, BatchImportResult } from '../types/gotypes'

interface UseBatchImportOptions {
  customer: string
  job: string
}

// A1 (design/2026-08-04-detach-ingest-from-pipelines.md): the
// auto_coder_batch_imports grouping row is retired, so there is no import
// history to list, poll or delete — an upload reports what it created in its
// own response and the pipelines list is the record from then on.
export const useBatchImport = (options: UseBatchImportOptions) => {
  const { customer, job } = options

  const snackbar = useSnackbar()
  const loading = useLoading()
  const queryClient = useQueryClient()

  // Preview CSV mutation
  const previewMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await axios.post<BatchImportPreviewResponse>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/batch-imports/preview`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      return response.data
    },
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  // Analyze CSV mutation
  const analyzeMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await axios.post<BatchImportAnalyzeResponse>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/batch-imports/analyze`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data
    },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  // Create batch import mutation
  const createMutation = useMutation({
    mutationFn: async ({
      file,
      urlColumn,
      labelColumns,
      frameworkIds,
      labels,
      billingTeamId,
    }: {
      file: File
      urlColumn: string
      labelColumns: string[]
      frameworkIds: string[]
      labels?: Record<string, string>
      /** The team every created pipeline is billed to; required when the customer has an open team. */
      billingTeamId?: string
    }) => {
      loading.setLoading(true)
      const formData = new FormData()
      formData.append('file', file)
      formData.append('url_column', urlColumn)
      labelColumns.forEach((c) => formData.append('label_columns[]', c))
      // Every bulk-upload pipeline is coded: F1 retired the `mode` field along
      // with the scrape_only pipeline mode it selected.
      frameworkIds.forEach((id) => formData.append('framework_ids[]', id))
      if (billingTeamId) formData.append('billing_team_id', billingTeamId)
      if (labels) {
        Object.entries(labels).forEach(([k, v]) => formData.append('labels', `${k}=${v}`))
      }
      const response = await axios.post<BatchImportResult>(
        `${API_BASE_URL}/autocoder/${customer}/${job}/batch-imports`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data
    },
    onSuccess: (data) => {
      loading.setLoading(false)
      snackbar.success(`Batch import started with ${data.pipelines_created} pipelines`)
      // Prefix invalidation: the exact list key (useAutoCoder.ts) carries page /
      // rows / labels / lifecycle state this mutation cannot know.
      queryClient.invalidateQueries({ queryKey: ['autocoder_pipelines'] })
    },
    onError: (error) => {
      loading.setLoading(false)
      snackbar.error(extractErrorMessage(error))
    },
  })

  return {
    previewCSV: previewMutation.mutateAsync,
    previewLoading: previewMutation.isPending,
    previewData: previewMutation.data,

    analyzeFile: analyzeMutation.mutateAsync,
    analyzeLoading: analyzeMutation.isPending,
    analyzeData: analyzeMutation.data,

    createBatchImport: createMutation.mutateAsync,
    createLoading: createMutation.isPending,
  }
}

export default useBatchImport
